// Tokens semânticos — o significado de uma cor (estado, gravidade, SLA, fase).
// Não dependem do produto: um "aviso" tem o mesmo aspeto no Nolens Fleet e em
// qualquer outro produto Nolens, e nunca usa a cor de acento do produto.

export const status = {
  success: { DEFAULT: '#10b981', light: '#d1fae5', dark: '#065f46' },
  warning: { DEFAULT: '#f59e0b', light: '#fef3c7', dark: '#78350f' },
  danger: { DEFAULT: '#ef4444', light: '#fee2e2', dark: '#7f1d1d' },
  info: { DEFAULT: '#3b82f6', light: '#dbeafe', dark: '#1e3a8a' },
  // SLA
  sla: {
    onTrack: '#10b981',
    atRisk: '#f59e0b',
    breached: '#ef4444',
  },
} as const

// Gravidade: só o que pede atenção tem cor (grave → âmbar, crítico →
// vermelho). Menor e moderado são neutros, distinguidos pelo texto e pelo
// tom do ponto. O texto do rótulo está sempre presente — nunca só a cor.
export const severity = {
  minor: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
  moderate: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-600' },
  major: { bg: 'bg-amber-50', text: 'text-amber-800', dot: 'bg-amber-600' },
  critical: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-600' },
} as const

// Fases agregadas (ver DamagePhase em @fleetops/types) — o que a UI mostra.
// `fill` alimenta os gráficos (recharts) para manter as cores consistentes
// entre badges, colunas kanban e dashboards.
//
// As quatro fases em curso são ordenadas: uma rampa neutra de um só tom,
// clara → escura (validada como ordinal — monótona, degraus visíveis, extremo
// claro ≥ 2:1 em branco). A paleta anterior (azul/roxo/verde/âmbar) falhava:
// "Em Análise" e "Orçamentação" ficavam iguais com deuteranopia (ΔE 1,3).
// Só os estados com significado têm cor: encerrado (sucesso) e disputado
// (atenção). O rótulo escrito acompanha sempre a cor.
export const damagePhaseConfig = {
  analysis: { label: 'Em Análise', color: 'bg-slate-100 text-slate-700', kanbanColor: 'border-transparent', fill: '#94a3b8' },
  quoting: { label: 'Orçamentação', color: 'bg-slate-100 text-slate-700', kanbanColor: 'border-transparent', fill: '#64748b' },
  approved: { label: 'Aprovado', color: 'bg-slate-100 text-slate-700', kanbanColor: 'border-transparent', fill: '#475569' },
  repair: { label: 'Em Reparação', color: 'bg-slate-100 text-slate-800', kanbanColor: 'border-transparent', fill: '#1e293b' },
  closed: { label: 'Encerrado', color: 'bg-emerald-50 text-emerald-800', kanbanColor: 'border-transparent', fill: '#059669' },
  disputed: { label: 'Disputado', color: 'bg-red-50 text-red-700', kanbanColor: 'border-transparent', fill: '#dc2626' },
} as const

// Estados granulares — usados apenas onde a precisão importa (histórico/
// auditoria); as vistas correntes usam damagePhaseConfig.
export const damageStatusConfig = {
  reported: { label: 'Reportado', color: 'bg-slate-100 text-slate-700', kanbanColor: 'border-slate-300' },
  under_review: { label: 'Em Análise', color: 'bg-blue-100 text-blue-700', kanbanColor: 'border-blue-400' },
  awaiting_estimate: { label: 'Aguarda Orçamento', color: 'bg-purple-100 text-purple-700', kanbanColor: 'border-purple-400' },
  estimate_received: { label: 'Orçamento Recebido', color: 'bg-indigo-100 text-indigo-700', kanbanColor: 'border-indigo-400' },
  partially_approved: { label: 'Parcialmente Aprovado', color: 'bg-teal-100 text-teal-700', kanbanColor: 'border-teal-400' },
  approved: { label: 'Aprovado', color: 'bg-emerald-100 text-emerald-700', kanbanColor: 'border-emerald-400' },
  in_repair: { label: 'Em Reparação', color: 'bg-amber-100 text-amber-700', kanbanColor: 'border-amber-400' },
  quality_check: { label: 'Controlo de Qualidade', color: 'bg-cyan-100 text-cyan-700', kanbanColor: 'border-cyan-400' },
  closed: { label: 'Encerrado', color: 'bg-green-100 text-green-700', kanbanColor: 'border-green-400' },
  disputed: { label: 'Disputado', color: 'bg-red-100 text-red-700', kanbanColor: 'border-red-400' },
} as const
